/**
 * Matters API - CRUD operations for legal matters/cases.
 */

import apiClient, { type RequestConfig } from './client';
import {
  isApiError,
  type Matter,
  type MatterReviewConfig,
  type CreateMatterRequest,
  type UpdateMatterRequest,
  type PaginatedResponse,
  type PaginationParams,
  type ReviewDisposition,
} from './types';

const ENDPOINT = '/matters';

export interface MatterCostSavingsResponse {
  matter_id: string;
  rate_per_doc: number;
  currency: string;
  computed_at: string;
  methodology_version: string;
  rate_provenance: string;
  review_policy?: MatterReviewConfig;
  counts: {
    total_docs: number;
    physical_document_rows?: number;
    reviewable_docs: number;
    suppressible_docs: number;
    thread_inclusives: number;
    active_thread_spares?: number;
    potential_thread_spares?: number;
    potential_thread_collapse_docs?: number;
    active_content_duplicates?: number;
    breakdown: {
      excluded_triage: number;
      thread_duplicate_spares: number;
      content_duplicates: number;
    };
  };
  savings_usd: {
    total_saved: number;
    breakdown: {
      triage_exclusion: number;
      email_thread_collapse: number;
      content_dedup: number;
    };
    potential_thread_collapse?: number;
    reduction_percentage: number;
  };
  pitch_line: string;
}

export interface MatterStatsResponse {
  [key: string]: unknown;
  matter_id: string;
  matter_name: string;
  status: string;
  phase: string;
  documents: {
    total: number;
    physical_rows?: number;
    processed: number;
    failed: number;
    skipped?: number;
    excluded?: number;
    queued?: number;
    pending?: number;
    processing?: number;
    embedded: number;
    embedding_completed?: number;
    non_embeddable: number;
    relevant: number;
    responsive?: number;
    non_responsive?: number;
    triaged?: number;
    privileged: number;
    duplicates: number;
    cross_matter_reused?: number;
    date_anomalies?: number;
  };
  volumes: {
    original_gb: number | null;
    processed_gb: number | null;
    culled_gb: number | null;
    total_gb: number;
    reduction_percentage: number | null;
  };
  roi: {
    estimated_savings: number | null;
    cost_per_gb: number;
    platform_cost_before: number;
    platform_cost_after: number;
  };
  telemetry: Record<string, unknown>;
  timestamps: {
    created_at: string;
    updated_at: string;
  };
  // Legacy compatibility fields consumed across older UI surfaces.
  document_count?: number;
  processed_count?: number;
  relevant_count?: number;
  privileged_count?: number;
  duplicate_count?: number;
  original_bytes?: number;
  processed_bytes?: number;
  reduction_percentage?: number;
  estimated_savings?: number;
}

export type MatterHealthStatus = 'ready' | 'partial' | 'blocked' | 'empty';

export interface MatterHealthGateResponse {
  id: string;
  label: string;
  status: MatterHealthStatus;
  value: string;
  detail: string;
}

export interface MatterHealthResponse {
  matter_id: string;
  score: number;
  status: MatterHealthStatus;
  label: string;
  gates: MatterHealthGateResponse[];
  blockers: string[];
  thread_proof: {
    representative_count: number;
    inclusive_count: number;
    suppressed_sibling_count: number;
    representative_examples: Array<{
      document_id: string;
      filename: string;
      subject?: string | null;
      thread_group_size: number;
      inclusive_reason?: string | null;
    }>;
    inspect_route: string;
    inspect_available: boolean;
    explanation: string;
    active_sibling_count?: number;
    potential_sibling_count?: number;
  };
  counts: Record<string, number>;
  review_policy?: MatterReviewConfig;
}

export interface ProofEnsureRequest {
  force?: boolean;
  ai_pre_review?: boolean;
  queue_export?: boolean;
  generate_report_pack?: boolean;
  export_format?: string;
  chronology_max_docs?: number;
  sweep_fidelity?: 'A' | 'B' | 'C';
  sweep_target_spec?: Record<string, unknown> | null;
  sweep_collection_id?: string | null;
  sweep_sample_size?: number | null;
  quote_id?: string | null;
}

export interface ProofEnsureResponse {
  matter_id: string;
  job_id?: string | null;
  run_id?: string | null;
  status: string;
  automation_enabled: boolean;
  actions_requested: string[];
  doc_count?: number;
  sweep_doc_limit?: number;
  large_matter?: boolean;
  sweep_scope_required?: boolean;
  proof_mode?: string;
  skip_reason?: string | null;
  proof_fingerprint?: string | null;
  reused_run_id?: string | null;
  output_only?: boolean;
  missing_outputs?: string[];
  paid_work_required?: boolean;
  requires_approval?: boolean;
  quote_id?: string | null;
  spend_request_id?: string | null;
  estimated_cost_usd?: number | null;
  joined_run_id?: string | null;
  blocked_reason?: string | null;
}

export interface MatterThreadMessage {
  id: string;
  filename: string;
  file_type?: string | null;
  comm_from?: string | null;
  comm_to?: string | null;
  comm_cc?: string | null;
  comm_bcc?: string | null;
  comm_subject?: string | null;
  comm_date?: string | null;
  comm_message_id?: string | null;
  snippet?: string | null;
  relevance_score?: number | null;
  triage_confidence?: number | null;
  privilege_status?: string | null;
  is_responsive?: boolean | null;
  exclusion_reason?: string | null;
  is_thread_inclusive?: boolean | null;
  inclusive_reason?: string | null;
  is_duplicate_spare?: boolean | null;
  collapse_reason?: string | null;
  thread_indent_level?: number | null;
  thread_hash?: string | null;
  thread_representative_id?: string | null;
  is_thread_representative?: boolean | null;
  thread_group_size?: number | null;
  review_disposition?: ReviewDisposition | null;
}

export interface MatterThreadDetailResponse {
  matter_id: string;
  thread_id: string;
  message_count: number;
  messages: MatterThreadMessage[];
}

export const mattersApi = {
  /**
   * List all matters with optional pagination and filters.
   */
  async list(
    params?: PaginationParams & {
      status?: string;
      phase?: string;
      search?: string;
    },
    config?: RequestConfig
  ): Promise<PaginatedResponse<Matter>> {
    const { data } = await apiClient.get<PaginatedResponse<Matter>>(ENDPOINT, {
      ...config,
      params: params as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Get a single matter by ID.
   */
  async get(id: string, config?: RequestConfig): Promise<Matter> {
    const { data } = await apiClient.get<Matter>(`${ENDPOINT}/${id}`, config);
    return data;
  },

  /**
   * Create a new matter.
   */
  async create(request: CreateMatterRequest, config?: RequestConfig): Promise<Matter> {
    const { data } = await apiClient.post<Matter>(ENDPOINT, request, config);
    return data;
  },

  /**
   * Update an existing matter.
   */
  async update(id: string, request: UpdateMatterRequest, config?: RequestConfig): Promise<Matter> {
    const { data } = await apiClient.patch<Matter>(`${ENDPOINT}/${id}`, request, config);
    return data;
  },

  /**
   * Archive a matter (soft delete). Can be reopened later.
   */
  async archive(id: string, config?: RequestConfig): Promise<Matter> {
    const { data } = await apiClient.post<Matter>(`${ENDPOINT}/${id}/archive`, {}, config);
    return data;
  },

  /**
   * Reopen an archived matter, restoring it to active status.
   */
  async reopen(id: string, config?: RequestConfig): Promise<Matter> {
    const { data } = await apiClient.post<Matter>(`${ENDPOINT}/${id}/reopen`, {}, config);
    return data;
  },

  /**
   * @deprecated Use archive() instead. This calls archive under the hood.
   */
  async delete(id: string, config?: RequestConfig): Promise<void> {
    await apiClient.post(`${ENDPOINT}/${id}/archive`, {}, config);
  },

  /**
   * Get matter statistics and volume metrics.
   */
  async getStats(id: string, config?: RequestConfig): Promise<MatterStatsResponse> {
    const { data } = await apiClient.get<Record<string, unknown>>(`${ENDPOINT}/${id}/statistics`, config);
    const documents = (data.documents as Record<string, unknown> | undefined) ?? {};
    const volumes = (data.volumes as Record<string, unknown> | undefined) ?? {};
    const roi = (data.roi as Record<string, unknown> | undefined) ?? {};

    return {
      ...(data as object),
      document_count: Number(data.document_count ?? documents.total ?? 0),
      processed_count: Number(data.processed_count ?? documents.processed ?? 0),
      relevant_count: Number(data.relevant_count ?? documents.relevant ?? 0),
      privileged_count: Number(data.privileged_count ?? documents.privileged ?? 0),
      duplicate_count: Number(data.duplicate_count ?? documents.duplicates ?? 0),
      original_bytes: Number(data.original_bytes ?? 0),
      processed_bytes: Number(data.processed_bytes ?? 0),
      reduction_percentage: Number(data.reduction_percentage ?? volumes.reduction_percentage ?? 0),
      estimated_savings: Number(data.estimated_savings ?? roi.estimated_savings ?? 0),
    } as MatterStatsResponse;
  },

  /**
   * Get matter-level downstream review savings from triage, thread collapse, and dedup.
   */
  async getCostSavings(
    id: string,
    params?: { rate_per_doc?: number },
    config?: RequestConfig
  ): Promise<MatterCostSavingsResponse> {
    const { data } = await apiClient.get<MatterCostSavingsResponse>(`${ENDPOINT}/${id}/cost-savings`, {
      ...config,
      params: params as Record<string, string | number | boolean | undefined> | undefined,
    });
    return data;
  },

  /**
   * Get matter health gates for readiness, reduction proof, QC, and export blockers.
   */
  async getHealth(id: string, config?: RequestConfig): Promise<MatterHealthResponse> {
    const { data } = await apiClient.get<MatterHealthResponse>(`${ENDPOINT}/${id}/health`, config);
    return data;
  },

  async ensureProof(
    id: string,
    request: ProofEnsureRequest,
    config?: RequestConfig
  ): Promise<ProofEnsureResponse> {
    const { data } = await apiClient.post<ProofEnsureResponse>(
      `${ENDPOINT}/${id}/proof/ensure`,
      request,
      config
    );
    return data;
  },

  async getThread(
    id: string,
    threadId: string,
    config?: RequestConfig
  ): Promise<MatterThreadDetailResponse> {
    const { data } = await apiClient.get<MatterThreadDetailResponse>(
      `${ENDPOINT}/${id}/threads/${threadId}`,
      config,
    );
    return data;
  },

  /**
   * Get processing history for a matter.
   */
  async getProcessingHistory(
    id: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<PaginatedResponse<{
    id: string;
    type: string;
    status: string;
    progress: number;
    created_at: string;
    completed_at?: string;
    input_count: number;
    output_count: number;
    created_by: string;
  }>> {
    const { data } = await apiClient.get<PaginatedResponse<{
      id: string;
      type: string;
      status: string;
      progress: number;
      created_at: string;
      completed_at?: string;
      input_count: number;
      output_count: number;
      created_by: string;
    }>>(`${ENDPOINT}/${id}/processing-history`, {
      ...config,
      params: params as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Get audit log for a matter.
   */
  async getAuditLog(
    id: string,
    params?: PaginationParams & {
      action?: string;
      user?: string;
      from_date?: string;
      to_date?: string;
    },
    config?: RequestConfig
  ): Promise<PaginatedResponse<{
    id: string;
    action: string;
    details: Record<string, unknown>;
    user_id: string;
    timestamp: string;
    entry_hash: string;
  }>> {
    const { data } = await apiClient.get<PaginatedResponse<{
      id: string;
      action: string;
      details: Record<string, unknown>;
      user_id: string;
      timestamp: string;
      entry_hash: string;
    }>>(`${ENDPOINT}/${id}/audit-log`, {
      ...config,
      params: params as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Update matter phase (intake -> processing -> review -> export).
   */
  async updatePhase(
    id: string,
    phase: 'intake' | 'processing' | 'review' | 'export',
    config?: RequestConfig
  ): Promise<Matter> {
    const { data } = await apiClient.patch<Matter>(
      `${ENDPOINT}/${id}/phase`,
      { phase },
      config
    );
    return data;
  },

  /**
   * Clone a matter (for testing or templates).
   */
  async clone(
    id: string,
    options?: {
      name?: string;
      include_documents?: boolean;
      include_settings?: boolean;
    },
    config?: RequestConfig
  ): Promise<Matter> {
    const { data } = await apiClient.post<Matter>(
      `${ENDPOINT}/${id}/clone`,
      options || {},
      config
    );
    return data;
  },

  /**
   * Retry/resume processing for a stuck matter.
   */
  async retry(id: string, config?: RequestConfig): Promise<{
    success: boolean;
    matter_id: string;
    matter_name: string;
    retry_summary: {
      documents_requeued: number;
      containers_requeued: number;
      pipeline_runs_requeued: number;
      failed_runs_found: number;
      total_stuck_before: number;
    };
    message: string;
  }> {
    const { data } = await apiClient.post<{
      success: boolean;
      matter_id: string;
      matter_name: string;
      retry_summary: {
        documents_requeued: number;
        containers_requeued: number;
        pipeline_runs_requeued: number;
        failed_runs_found: number;
        total_stuck_before: number;
      };
      message: string;
    }>(`${ENDPOINT}/${id}/retry`, {}, config);
    return data;
  },

  /**
   * List all purge jobs (destruction receipts) in the system.
   */
  async listPurgeStatuses(config?: RequestConfig): Promise<any[]> {
    const { data } = await apiClient.get<any[]>(`${ENDPOINT}/purge-status`, config);
    return data;
  },

  /**
   * Get purge status for a specific matter.
   * Returns the purge job if the matter was deleted, or null if not found.
   */
  async getPurgeStatus(matterId: string, config?: RequestConfig): Promise<{
    purge_job_id: string;
    matter_id: string;
    matter_name: string;
    status: string;
    requested_by: string | null;
    requested_at: string | null;
    completed_at: string | null;
    documents_deleted: number | null;
    receipt_hash: string | null;
  } | null> {
    try {
      const { data } = await apiClient.get<{
        purge_job_id: string;
        matter_id: string;
        matter_name: string;
        status: string;
        requested_by: string | null;
        requested_at: string | null;
        completed_at: string | null;
        documents_deleted: number | null;
        receipt_hash: string | null;
      }>(`${ENDPOINT}/${matterId}/purge-status`, config);
      return data;
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 404) {
        return null;  // No purge job found
      }
      throw error;
    }
  },

  /**
   * Generate a statistically significant validation sample for the matter.
   */
  async generateValidationSample(
    id: string,
    params?: { 
      confidence?: number;
      margin?: number;
    },
    config?: RequestConfig
  ): Promise<any> {
    const { data } = await apiClient.post(`${ENDPOINT}/${id}/validation-sample`, {}, {
      ...config,
      params: params as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Place a legal hold on a matter.
   */
  async placeHold(
    matterId: string,
    scope: { 
      custodians?: string[]; 
      date_range?: string[]; 
      keywords?: string[] 
    },
    config?: RequestConfig
  ): Promise<any> {
    const { data } = await apiClient.post(`${ENDPOINT}/${matterId}/hold`, scope, config);
    return data;
  },

  /**
   * Release a legal hold.
   */
  async releaseHold(
    holdId: string,
    reason: string,
    config?: RequestConfig
  ): Promise<any> {
    const { data } = await apiClient.post(`/holds/${holdId}/release`, { reason }, {
      ...config,
      params: { reason } // API uses Query param for reason? No, check backend/app/api/holds.py
    });
    return data;
  },

  /**
   * Update forensic engine configuration (agent concurrency, budget).
   */
  async updateEngineConfig(
    id: string,
    request: {
      agent_concurrency_limit?: number;
      compute_budget_usd?: number;
      engine_tier?: 'standard' | 'elite' | 'custom';
    },
    config?: RequestConfig
  ): Promise<Matter> {
    const { data } = await apiClient.patch<Matter>(
      `${ENDPOINT}/${id}/engine-config`,
      request,
      config
    );
    return data;
  },

  /**
   * Get current telemetry/system stats for processing.
   */
  async getTelemetry(id: string, config?: RequestConfig): Promise<{
    cpu_percent: number;
    memory_percent: number;
    active_workers: number;
    architecture: string;
    available_cores: number;
  }> {
    const { data } = await apiClient.get<{
      cpu_percent: number;
      memory_percent: number;
      active_workers: number;
      architecture: string;
      available_cores: number;
    }>(`${ENDPOINT}/${id}/telemetry`, config);
    return data;
  },

  /**
   * Get AI/compute spend for a matter.
   */
  /**
   * Get search configuration for a matter.
   */
  async getSearchConfig(id: string, config?: RequestConfig): Promise<{
    mode: string;
    embeddings_enabled: boolean;
    reranking_enabled: boolean;
    min_relevance_score: number;
    include_transcripts: boolean;
    query_expansion: boolean;
    top_k: number;
    rerank_limit: number;
    ef_search: number;
    reranker_concurrency: number;
  }> {
    const { data } = await apiClient.get<{
      mode: string;
      embeddings_enabled: boolean;
      reranking_enabled: boolean;
      min_relevance_score: number;
      include_transcripts: boolean;
      query_expansion: boolean;
      top_k: number;
      rerank_limit: number;
      ef_search: number;
      reranker_concurrency: number;
    }>(`${ENDPOINT}/${id}/search-config`, config);
    return data;
  },

  /**
   * Update search configuration for a matter (partial patch).
   */
  async updateSearchConfig(
    id: string,
    patch: Record<string, any>,
    config?: RequestConfig
  ): Promise<{
    mode: string;
    embeddings_enabled: boolean;
    reranking_enabled: boolean;
    min_relevance_score: number;
    include_transcripts: boolean;
    query_expansion: boolean;
    top_k: number;
    rerank_limit: number;
    ef_search: number;
    reranker_concurrency: number;
  }> {
    const { data } = await apiClient.patch<{
      mode: string;
      embeddings_enabled: boolean;
      reranking_enabled: boolean;
      min_relevance_score: number;
      include_transcripts: boolean;
      query_expansion: boolean;
      top_k: number;
      rerank_limit: number;
      ef_search: number;
      reranker_concurrency: number;
    }>(`${ENDPOINT}/${id}/search-config`, patch, config);
    return data;
  },

  async getSpend(id: string, config?: RequestConfig): Promise<{
    matter_id: string;
    budget: {
      limit_usd: number;
      spent_usd: number;
      remaining_usd: number;
      percent_used: number;
      status: 'ok' | 'critical' | 'depleted';
    };
    breakdown: Array<{
      operation: string;
      model: string;
      cost_usd: number;
      input_tokens: number;
      output_tokens: number;
      calls: number;
    }>;
    total_cost_usd: number;
  }> {
    const { data } = await apiClient.get<{
      matter_id: string;
      budget: {
        limit_usd: number;
        spent_usd: number;
        remaining_usd: number;
        percent_used: number;
        status: 'ok' | 'critical' | 'depleted';
      };
      breakdown: Array<{
        operation: string;
        model: string;
        cost_usd: number;
        input_tokens: number;
        output_tokens: number;
        calls: number;
      }>;
      total_cost_usd: number;
    }>(`${ENDPOINT}/${id}/spend`, config);
    return data;
  },
};

export default mattersApi;
